// What the brief's small-LLM lines are written from — its headline, its bottom line and its
// insight headings — read off a forecast the same way whether the report window is open or
// the forecast has only just finished. One function for both, so a line started early is the
// very one the brief asks for (same inputs, same cache key).

import { runAuthoredNoForecast } from "../forecast/unauthoredRun.js";
import { extractMultiway } from "../forecast/multiway.js";
import { evidenceCardsOf } from "../forecast/evidenceCards.js";
import { crossInsightsOf } from "../forecast/crossInsights.js";
import { reportAnalysisOf } from "../forecast/reportAnalysis.js";
import { assessmentSummaryOf } from "../forecast/summaryPost.js";
import { cleanForecastSummary } from "./blocks.js";
import { buildBrief } from "./brief.js";

/**
 * The inputs of a report's brief lines; null when the report would not show a brief (no report
 * yet, or a run that authored no forecast). Every forecaster's report reads as the brief: an
 * older report (no evidence cards, insights or analysis) still has a question, a probability
 * and a written summary to write from.
 *
 * @param {object|null} source the report source: a forecast with `contract` and `report`
 *   (callers may still pass `{engine}` second; the forecaster no longer decides)
 * @returns {null | {
 *   conclusion: {question: string, multi: boolean, lead: object, runnerUp: object|null,
 *     probability: number|null, unit: string, criteria: string} | null,
 *   bottomLine: {question: string, narrative: string, uncertainties: string[]},
 *   insights: {question: string, insights: Array<{ref: string, text: string}>|null},
 * }}
 */
export function briefLlmInputsOf(source) {
  if (!source) return null;
  const c = source.contract || {};
  const mr = source.multi_report || c.multi_report || source.serverContext?.multi_report || null;
  const r = source.report || source.serverContext?.report || null;
  if ((!r && !mr) || runAuthoredNoForecast(source)) return null;
  const question = c.question || c.title || "";
  const probability = typeof r?.probability === "number" ? r.probability : null;
  // The forecast's own outcomes, as the brief reads them (never the display distribution).
  const b = buildBrief({ probability, buckets: extractMultiway(source)?.buckets ?? null });
  return {
    // No written headline when the catch-all outweighs the leader (or leads): the brief's own
    // "leads the named field" sentence is the only honest one there.
    conclusion:
      b.lead && !b.otherLeads && b.lead.isOther !== true
        ? {
            question,
            multi: !!b.multi,
            lead: b.lead,
            runnerUp: b.distribution?.[1] ?? null,
            probability,
            unit: c.forecast_value?.outcome_space?.unit || "",
            criteria: c.resolution_criteria || "",
          }
        : null,
    bottomLine: {
      question,
      narrative: cleanForecastSummary(
        r?.analysis?.narrative || mr?.forecast_summary || assessmentSummaryOf(source) || "",
      ),
      uncertainties: (r && reportAnalysisOf(r)?.contentUncertainties) || [],
    },
    insights: { question, insights: r ? crossInsightsOf(r, evidenceCardsOf(r)) : null },
  };
}
